import type { GameConfig, Player, RenderSnapshot, Vehicle } from "./types";

interface RoadLayout {
  left: number;
  right: number;
  top: number;
  bottom: number;
  laneWidth: number;
  centers: number[];
}

export class CanvasRenderer {
  private context: CanvasRenderingContext2D;
  private width = 1;
  private height = 1;
  private dpr = 1;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly config: GameConfig
  ) {
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas 2D context is unavailable.");
    }
    this.context = context;
    this.resize();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.width = Math.max(320, rect.width);
    this.height = Math.max(520, rect.height);
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.floor(this.width * this.dpr);
    this.canvas.height = Math.floor(this.height * this.dpr);
    this.context.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  getLayout(): RoadLayout {
    const roadWidth = Math.min(this.width * 0.82, 720);
    const left = (this.width - roadWidth) / 2;
    const laneWidth = roadWidth / this.config.laneCount;
    return {
      left,
      right: left + roadWidth,
      top: 0,
      bottom: this.height,
      laneWidth,
      centers: Array.from({ length: this.config.laneCount }, (_, lane) => left + laneWidth * (lane + 0.5))
    };
  }

  playerY(): number {
    return this.height * 0.76;
  }

  laneCenter(lane: number): number {
    return this.getLayout().centers[lane] ?? this.width / 2;
  }

  draw(snapshot: RenderSnapshot): void {
    const layout = this.getLayout();
    this.drawSky(snapshot.elapsed);
    this.drawCity(snapshot.elapsed);
    this.drawRoadside(layout, snapshot.roadOffset, snapshot.elapsed);
    this.drawRoad(layout, snapshot.roadOffset);
    this.drawSigns(layout, snapshot.roadOffset);

    const sortedVehicles = [...snapshot.vehicles].sort((a, b) => a.y - b.y);
    for (const vehicle of sortedVehicles) {
      this.drawVehicle(vehicle, layout.centers[vehicle.lane], vehicle.y, false);
    }

    this.drawPlayer(snapshot.player, this.playerY());
    this.drawSpeedLines(snapshot.player.speed / this.config.maxSpeed);

    if (snapshot.flash > 0) {
      this.context.fillStyle = `rgba(255, 70, 56, ${0.32 * snapshot.flash})`;
      this.context.fillRect(0, 0, this.width, this.height);
    }

    if (snapshot.state === "paused") {
      this.context.fillStyle = "rgba(2, 8, 10, 0.22)";
      this.context.fillRect(0, 0, this.width, this.height);
    }
  }

  private drawSky(elapsed: number): void {
    const gradient = this.context.createLinearGradient(0, 0, 0, this.height);
    gradient.addColorStop(0, "#102d34");
    gradient.addColorStop(0.5, "#07161a");
    gradient.addColorStop(1, "#030609");
    this.context.fillStyle = gradient;
    this.context.fillRect(0, 0, this.width, this.height);

    this.context.fillStyle = "rgba(255, 224, 136, 0.82)";
    this.context.beginPath();
    this.context.arc(this.width * 0.78, this.height * 0.12, 28 + Math.sin(elapsed * 0.4) * 2, 0, Math.PI * 2);
    this.context.fill();

    this.context.fillStyle = "rgba(255, 244, 196, 0.42)";
    for (let i = 0; i < 38; i += 1) {
      const x = (i * 97 + Math.sin(elapsed * 0.05 + i) * 14) % this.width;
      const y = 24 + ((i * 53) % Math.max(120, this.height * 0.28));
      const radius = 0.6 + (i % 3) * 0.35;
      this.context.beginPath();
      this.context.arc(x, y, radius, 0, Math.PI * 2);
      this.context.fill();
    }
  }

  private drawCity(elapsed: number): void {
    const horizon = this.height * 0.33;
    const waterGradient = this.context.createLinearGradient(0, horizon, 0, this.height);
    waterGradient.addColorStop(0, "rgba(7, 41, 48, 0.72)");
    waterGradient.addColorStop(0.4, "rgba(4, 22, 28, 0.86)");
    waterGradient.addColorStop(1, "rgba(2, 8, 10, 0.94)");
    this.context.fillStyle = waterGradient;
    this.context.fillRect(0, horizon, this.width, this.height - horizon);

    this.context.fillStyle = "rgba(31, 58, 64, 0.7)";
    for (let i = 0; i < 28; i += 1) {
      const width = 22 + ((i * 17) % 40);
      const height = 42 + ((i * 29) % 116);
      const x = ((i * 73 + elapsed * 7) % (this.width + 120)) - 80;
      this.context.fillRect(x, horizon - height, width, height);

      this.context.fillStyle = "rgba(255, 218, 132, 0.28)";
      for (let row = 0; row < Math.floor(height / 22); row += 1) {
        if ((row + i) % 3 === 0) {
          this.context.fillRect(x + 7, horizon - height + 11 + row * 20, 4, 3);
          this.context.fillRect(x + width - 12, horizon - height + 11 + row * 20, 4, 3);
        }
      }
      this.context.fillStyle = "rgba(31, 58, 64, 0.7)";
    }

    this.context.strokeStyle = "rgba(92, 197, 185, 0.35)";
    this.context.lineWidth = 3;
    this.context.beginPath();
    this.context.moveTo(this.width * 0.05, horizon + 12);
    this.context.bezierCurveTo(
      this.width * 0.28,
      horizon - 44,
      this.width * 0.72,
      horizon - 44,
      this.width * 0.95,
      horizon + 12
    );
    this.context.stroke();

    this.context.strokeStyle = "rgba(92, 197, 185, 0.22)";
    this.context.lineWidth = 1;
    for (let i = 0; i < 9; i += 1) {
      const y = horizon + 36 + i * 22 + Math.sin(elapsed * 0.8 + i) * 2;
      this.context.beginPath();
      this.context.moveTo(0, y);
      this.context.bezierCurveTo(this.width * 0.32, y + 7, this.width * 0.64, y - 9, this.width, y + 3);
      this.context.stroke();
    }
  }

  private drawRoadside(layout: RoadLayout, roadOffset: number, elapsed: number): void {
    const shoulderWidth = Math.max(70, layout.laneWidth * 0.55);
    const leftShoulder = layout.left - shoulderWidth;
    const rightShoulder = layout.right;

    const shoulderGradient = this.context.createLinearGradient(leftShoulder, 0, layout.left, 0);
    shoulderGradient.addColorStop(0, "rgba(10, 35, 34, 0.15)");
    shoulderGradient.addColorStop(1, "rgba(36, 48, 48, 0.75)");
    this.context.fillStyle = shoulderGradient;
    this.context.fillRect(leftShoulder, 0, shoulderWidth, this.height);

    const rightGradient = this.context.createLinearGradient(layout.right, 0, layout.right + shoulderWidth, 0);
    rightGradient.addColorStop(0, "rgba(36, 48, 48, 0.75)");
    rightGradient.addColorStop(1, "rgba(10, 35, 34, 0.15)");
    this.context.fillStyle = rightGradient;
    this.context.fillRect(rightShoulder, 0, shoulderWidth, this.height);

    this.drawGuardrail(layout.left - 20, roadOffset, -1);
    this.drawGuardrail(layout.right + 20, roadOffset, 1);
    this.drawStreetLights(layout, roadOffset, elapsed);
    this.drawRoadsideMarkers(layout, roadOffset);
  }

  private drawRoad(layout: RoadLayout, roadOffset: number): void {
    const roadGradient = this.context.createLinearGradient(layout.left, 0, layout.right, 0);
    roadGradient.addColorStop(0, "#1c282d");
    roadGradient.addColorStop(0.5, "#26343a");
    roadGradient.addColorStop(1, "#1c282d");
    this.context.fillStyle = roadGradient;
    this.context.fillRect(layout.left, layout.top, layout.right - layout.left, layout.bottom);

    this.context.fillStyle = "rgba(255, 255, 255, 0.025)";
    for (let i = 0; i < 42; i += 1) {
      const x = layout.left + ((i * 71) % Math.max(1, layout.right - layout.left));
      const y = ((i * 137 + roadOffset * 0.22) % (this.height + 80)) - 40;
      this.context.fillRect(x, y, 18 + (i % 4) * 12, 1);
    }

    this.context.fillStyle = "rgba(10, 14, 16, 0.18)";
    for (let i = 0; i < 14; i += 1) {
      const x = layout.left + 24 + ((i * 113) % Math.max(1, layout.right - layout.left - 48));
      const y = ((i * 211 + roadOffset * 0.38) % (this.height + 140)) - 70;
      this.context.beginPath();
      this.context.ellipse(x, y, 32 + (i % 3) * 16, 4 + (i % 2) * 2, 0.2, 0, Math.PI * 2);
      this.context.fill();
    }

    this.context.fillStyle = "rgba(255, 255, 255, 0.1)";
    this.context.fillRect(layout.left - 6, 0, 3, this.height);
    this.context.fillRect(layout.right + 3, 0, 3, this.height);

    this.context.fillStyle = "rgba(255, 210, 86, 0.85)";
    for (let laneEdge of [layout.left + 8, layout.right - 12]) {
      for (let i = 0; i < 16; i += 1) {
        const y = ((i * 96 + roadOffset * 0.86) % (this.height + 80)) - 40;
        this.context.fillRect(laneEdge, y, 4, 18);
      }
    }

    const dashHeight = 58;
    const gap = 34;
    const offset = roadOffset % (dashHeight + gap);

    for (let lane = 1; lane < this.config.laneCount; lane += 1) {
      const x = layout.left + lane * layout.laneWidth;
      this.context.strokeStyle = "rgba(255, 255, 255, 0.56)";
      this.context.lineWidth = 3;
      this.context.setLineDash([dashHeight, gap]);
      this.context.lineDashOffset = -offset;
      this.context.beginPath();
      this.context.moveTo(x, -dashHeight);
      this.context.lineTo(x, this.height + dashHeight);
      this.context.stroke();
      this.context.setLineDash([]);
    }

    for (let i = 0; i < 18; i += 1) {
      const y = ((i * 120 + roadOffset * 0.55) % (this.height + 140)) - 90;
      const alpha = Math.max(0, 1 - y / this.height) * 0.22;
      this.context.fillStyle = `rgba(255, 255, 255, ${alpha})`;
      this.context.fillRect(layout.left + 18, y, layout.laneWidth * 0.34, 2);
      this.context.fillRect(layout.right - layout.laneWidth * 0.34 - 18, y + 34, layout.laneWidth * 0.32, 2);
    }
  }

  private drawSigns(layout: RoadLayout, roadOffset: number): void {
    const signY = ((roadOffset * 0.86) % (this.height + 520)) - 340;
    const x = layout.right + 18;
    this.context.save();
    this.context.translate(x, signY);
    this.context.fillStyle = "#0f7d72";
    this.context.strokeStyle = "rgba(255,255,255,0.8)";
    this.context.lineWidth = 2;
    this.roundRect(0, 0, 132, 56, 7);
    this.context.fill();
    this.context.stroke();
    this.context.fillStyle = "#ffffff";
    this.context.font = "800 15px Inter, sans-serif";
    this.context.fillText("Kopru", 14, 23);
    this.context.font = "700 11px Inter, sans-serif";
    this.context.fillText("TEM  O-2", 14, 42);
    this.context.restore();

    const gantryY = ((roadOffset * 0.96) % (this.height + 900)) - 680;
    this.context.save();
    this.context.translate(0, gantryY);
    this.context.strokeStyle = "rgba(169, 188, 190, 0.55)";
    this.context.lineWidth = 5;
    this.context.beginPath();
    this.context.moveTo(layout.left - 26, 0);
    this.context.lineTo(layout.right + 26, 0);
    this.context.moveTo(layout.left - 20, 0);
    this.context.lineTo(layout.left - 20, 70);
    this.context.moveTo(layout.right + 20, 0);
    this.context.lineTo(layout.right + 20, 70);
    this.context.stroke();

    const signWidth = Math.min(220, layout.laneWidth * 1.35);
    this.context.fillStyle = "#126f67";
    this.context.strokeStyle = "rgba(255,255,255,0.7)";
    this.context.lineWidth = 2;
    this.roundRect(layout.left + layout.laneWidth * 0.38, 12, signWidth, 52, 6);
    this.context.fill();
    this.context.stroke();
    this.roundRect(layout.right - layout.laneWidth * 1.72, 12, signWidth, 52, 6);
    this.context.fill();
    this.context.stroke();
    this.context.fillStyle = "#ffffff";
    this.context.font = "800 14px Inter, sans-serif";
    this.context.fillText("Maslak", layout.left + layout.laneWidth * 0.38 + 15, 34);
    this.context.fillText("Levent", layout.right - layout.laneWidth * 1.72 + 15, 34);
    this.context.font = "700 11px Inter, sans-serif";
    this.context.fillText("FSM  8 km", layout.left + layout.laneWidth * 0.38 + 15, 51);
    this.context.fillText("Besiktas", layout.right - layout.laneWidth * 1.72 + 15, 51);
    this.context.restore();
  }

  private drawPlayer(player: Player, y: number): void {
    this.drawVehicle(
      {
        id: -1,
        lane: player.lane,
        y,
        speed: player.speed,
        targetSpeed: player.speed,
        length: 92,
        width: 46,
        type: "sedan",
        reactionDelay: 0,
        reactionTimer: 0,
        behavior: "steady",
        color: "#53f4a5",
        passed: false,
        nearMissed: false
      },
      player.visualX,
      y,
      true
    );

    if (player.boostTimer > 0) {
      this.context.fillStyle = "rgba(92, 241, 181, 0.26)";
      this.context.beginPath();
      this.context.ellipse(player.visualX, y + 64, 24, 82, 0, 0, Math.PI * 2);
      this.context.fill();
    }
  }

  private drawVehicle(vehicle: Vehicle, x: number, y: number, isPlayer: boolean): void {
    const halfW = vehicle.width / 2;
    const halfL = vehicle.length / 2;
    const radius = vehicle.type === "bus" || vehicle.type === "van" ? 7 : vehicle.type === "hatchback" ? 12 : 10;

    this.context.save();
    this.context.translate(x, y);

    this.context.fillStyle = "rgba(0, 0, 0, 0.28)";
    this.roundRect(-halfW - 3, -halfL + 8, vehicle.width + 6, vehicle.length + 8, radius + 2);
    this.context.fill();

    this.context.fillStyle = vehicle.color;
    this.roundRect(-halfW, -halfL, vehicle.width, vehicle.length, radius);
    this.context.fill();

    const shine = this.context.createLinearGradient(-halfW, -halfL, halfW, halfL);
    shine.addColorStop(0, "rgba(255,255,255,0.42)");
    shine.addColorStop(0.28, "rgba(255,255,255,0.08)");
    shine.addColorStop(1, "rgba(0,0,0,0.18)");
    this.context.fillStyle = shine;
    this.roundRect(-halfW, -halfL, vehicle.width, vehicle.length, radius);
    this.context.fill();

    this.drawVehicleWindows(vehicle, halfW, halfL, isPlayer);
    this.drawVehicleTypeDetails(vehicle, halfW, halfL, isPlayer);

    this.context.fillStyle = isPlayer ? "#e8fff6" : "#ffe8a3";
    this.context.fillRect(-halfW + 7, -halfL - 1, 10, 5);
    this.context.fillRect(halfW - 17, -halfL - 1, 10, 5);
    this.context.fillStyle = "#ff465a";
    this.context.fillRect(-halfW + 8, halfL - 4, 9, 5);
    this.context.fillRect(halfW - 17, halfL - 4, 9, 5);

    this.context.restore();
  }

  private drawVehicleWindows(vehicle: Vehicle, halfW: number, halfL: number, isPlayer: boolean): void {
    this.context.fillStyle = isPlayer ? "#083b35" : "rgba(3, 11, 14, 0.72)";

    if (vehicle.type === "bus") {
      for (let i = 0; i < 4; i += 1) {
        const y = -halfL + 20 + i * 26;
        this.roundRect(-halfW + 8, y, vehicle.width - 16, 14, 4);
        this.context.fill();
      }
      return;
    }

    if (vehicle.type === "van") {
      this.roundRect(-halfW + 8, -halfL + 18, vehicle.width - 16, 22, 5);
      this.context.fill();
      this.roundRect(-halfW + 10, halfL - 42, vehicle.width - 20, 18, 4);
      this.context.fill();
      return;
    }

    if (vehicle.type === "hatchback") {
      this.roundRect(-halfW + 8, -halfL + 14, vehicle.width - 16, 20, 6);
      this.context.fill();
      this.roundRect(-halfW + 10, halfL - 28, vehicle.width - 20, 14, 5);
      this.context.fill();
      return;
    }

    const rearWindowHeight = vehicle.type === "suv" ? 24 : 20;
    this.roundRect(-halfW + 8, -halfL + 16, vehicle.width - 16, 24, 6);
    this.context.fill();
    this.roundRect(-halfW + 8, halfL - 34, vehicle.width - 16, rearWindowHeight, 6);
    this.context.fill();
  }

  private drawVehicleTypeDetails(vehicle: Vehicle, halfW: number, halfL: number, isPlayer: boolean): void {
    if (isPlayer) return;

    if (vehicle.type === "bus") {
      this.context.fillStyle = "rgba(255,255,255,0.22)";
      this.context.fillRect(-halfW + 5, -halfL + 10, 3, vehicle.length - 20);
      this.context.fillRect(halfW - 8, -halfL + 10, 3, vehicle.length - 20);
      this.context.fillStyle = "rgba(7, 12, 14, 0.32)";
      this.roundRect(-halfW + 12, halfL - 30, vehicle.width - 24, 8, 4);
      this.context.fill();
      return;
    }

    if (vehicle.type === "suv") {
      this.context.fillStyle = "rgba(0,0,0,0.22)";
      this.context.fillRect(-halfW - 2, -halfL + 22, 4, 16);
      this.context.fillRect(halfW - 2, -halfL + 22, 4, 16);
      this.context.fillRect(-halfW - 2, halfL - 38, 4, 16);
      this.context.fillRect(halfW - 2, halfL - 38, 4, 16);
      return;
    }

    if (vehicle.type === "van") {
      this.context.fillStyle = "rgba(255,255,255,0.2)";
      this.context.fillRect(-halfW + 7, -halfL + 48, vehicle.width - 14, 2);
    }
  }

  private drawSpeedLines(speedRatio: number): void {
    this.context.strokeStyle = `rgba(120, 246, 211, ${Math.max(0, speedRatio - 0.45) * 0.35})`;
    this.context.lineWidth = 2;
    for (let i = 0; i < 18; i += 1) {
      const x = ((i * 89) % this.width) + Math.sin(i) * 18;
      const y = ((i * 173 + speedRatio * 700) % this.height) - 30;
      this.context.beginPath();
      this.context.moveTo(x, y);
      this.context.lineTo(x - 20, y + 90);
      this.context.stroke();
    }
  }

  private drawGuardrail(x: number, roadOffset: number, side: -1 | 1): void {
    this.context.strokeStyle = "rgba(180, 198, 200, 0.55)";
    this.context.lineWidth = 4;
    this.context.beginPath();
    this.context.moveTo(x, 0);
    this.context.lineTo(x, this.height);
    this.context.stroke();

    this.context.strokeStyle = "rgba(28, 40, 42, 0.65)";
    this.context.lineWidth = 2;
    for (let i = 0; i < 18; i += 1) {
      const y = ((i * 82 + roadOffset * 0.75) % (this.height + 90)) - 45;
      this.context.beginPath();
      this.context.moveTo(x + side * 5, y);
      this.context.lineTo(x + side * 24, y + 34);
      this.context.stroke();
    }
  }

  private drawStreetLights(layout: RoadLayout, roadOffset: number, elapsed: number): void {
    for (let i = 0; i < 8; i += 1) {
      const y = ((i * 190 + roadOffset * 0.68) % (this.height + 220)) - 110;
      const side = i % 2 === 0 ? -1 : 1;
      const baseX = side < 0 ? layout.left - 58 : layout.right + 58;
      const armX = baseX + side * -46;

      this.context.strokeStyle = "rgba(135, 155, 158, 0.55)";
      this.context.lineWidth = 4;
      this.context.beginPath();
      this.context.moveTo(baseX, y + 86);
      this.context.lineTo(baseX, y);
      this.context.quadraticCurveTo(baseX, y - 22, armX, y - 22);
      this.context.stroke();

      const glow = this.context.createRadialGradient(armX, y - 20, 2, armX, y - 20, 74);
      glow.addColorStop(0, `rgba(255, 220, 130, ${0.22 + Math.sin(elapsed + i) * 0.03})`);
      glow.addColorStop(1, "rgba(255, 220, 130, 0)");
      this.context.fillStyle = glow;
      this.context.beginPath();
      this.context.arc(armX, y - 20, 74, 0, Math.PI * 2);
      this.context.fill();

      this.context.fillStyle = "rgba(255, 233, 174, 0.86)";
      this.context.beginPath();
      this.context.ellipse(armX, y - 20, 10, 5, 0, 0, Math.PI * 2);
      this.context.fill();
    }
  }

  private drawRoadsideMarkers(layout: RoadLayout, roadOffset: number): void {
    for (let i = 0; i < 18; i += 1) {
      const y = ((i * 78 + roadOffset * 0.94) % (this.height + 100)) - 50;
      const leftX = layout.left - 42;
      const rightX = layout.right + 34;
      this.context.fillStyle = i % 2 === 0 ? "#e94141" : "#f6f1df";
      this.context.fillRect(leftX, y, 8, 18);
      this.context.fillRect(rightX, y + 28, 8, 18);
    }
  }

  private roundRect(x: number, y: number, width: number, height: number, radius: number): void {
    this.context.beginPath();
    this.context.roundRect(x, y, width, height, radius);
  }
}
